import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import {
  BarChart3 as BarChartIcon,
  CalendarRange as CalendarRangeIcon,
  Copy as CopyIcon,
  Download as DownloadIcon,
  ExternalLink as ExternalLinkIcon,
  FileText as FileTextIcon,
  Info as InfoIcon,
  Percent as PercentIcon,
  Trash2 as DeleteIcon,
  Edit3 as EditIcon,
  Plus as PlusIcon,
  Search as SearchIcon,
  UploadCloud as UploadCloudIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  createIndicatori,
  deleteIndicatori,
  importIndicatoriCsv,
  downloadIndicatoriReportPdf,
  listIndicatori,
  listIndicatoriTrimesters,
  listIndicatoriYears,
  updateIndicatori
} from '../services/gtlTegmApi';

const OFFICIAL_TEGM_URL = 'https://www.bancaditalia.it/compiti/vigilanza/compiti-vigilanza/tegm/';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundImage: 'none',
  backgroundColor: theme.palette.background.paper
}));

const formatDateIt = (value) => {
  if (!value) return '-';
  const raw = String(value);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const formatDateItInput = (value) => {
  if (!value) return '';
  const raw = String(value);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const normalizeDateToIso = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const mIso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (mIso) return `${mIso[1]}-${mIso[2]}-${mIso[3]}`;
  const mIt = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (mIt) return `${mIt[3]}-${mIt[2]}-${mIt[1]}`;
  const mItDash = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (mItDash) return `${mItDash[3]}-${mItDash[2]}-${mItDash[1]}`;
  return raw;
};

const extractYear = (value) => {
  if (!value) return '';
  if (value instanceof Date) return String(value.getFullYear());
  const raw = String(value);
  const mIso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (mIso?.[1]) return mIso[1];
  const mIt = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (mIt?.[3]) return mIt[3];
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return String(parsed.getFullYear());
  const mAny = raw.match(/(\d{4})/);
  return mAny?.[1] || '';
};

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const normalizeText = (value) => {
  const raw = String(value ?? '');
  if (!raw) return '';
  return raw.replace(/Â°/g, '°').replace(/(\d)\uFFFD/g, '$1°');
};

const extractFilename = (headerValue, fallback) => {
  const raw = String(headerValue || '');
  const utf8Match = raw.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const simpleMatch = raw.match(/filename="?([^"]+)"?/i);
  return simpleMatch?.[1] || fallback;
};

const getTodayYmd = () => {
  const d = new Date();
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatPercent = (value, digits = 3) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const nf = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
  return `${nf.format(n)}%`;
};

export default function AttiAiIndicatoriTrimestraliPage() {
  const theme = useTheme();
  const accentColor = theme.palette.info.main;
  const [officialSourceOpen, setOfficialSourceOpen] = useState(true);
  const [infoOpen, setInfoOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reporting, setReporting] = useState(false);

  const [trimestersLoading, setTrimestersLoading] = useState(false);
  const [trimesters, setTrimesters] = useState([]);
  const [selectedTrimestre, setSelectedTrimestre] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearsLoading, setYearsLoading] = useState(false);
  const [years, setYears] = useState([]);

  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState('');
  const [rows, setRows] = useState([]);

  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({
    data_inizio_trimestre: '',
    data_fine_trimestre: '',
    commissione_massimo_scoperto_media: '',
    maggiorazione_mora_media: '',
    fonte_file: 'manuale'
  });

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadTrimesters = useCallback(async () => {
    setTrimestersLoading(true);
    try {
      const data = await listIndicatoriTrimesters();
      const nextTrimesters = Array.isArray(data?.rows) ? data.rows : [];
      setTrimesters(nextTrimesters);
      setRowsError('');
      setSelectedTrimestre((prev) => {
        if (prev?.data_inizio_trimestre) {
          const stillExists = nextTrimesters.some((t) => t.data_inizio_trimestre === prev.data_inizio_trimestre);
          if (stillExists) return prev;
        }
        return nextTrimesters[0] || null;
      });
    } catch (e) {
      setRowsError('Errore nel caricamento dei trimestri');
    } finally {
      setTrimestersLoading(false);
    }
  }, []);

  const loadYears = useCallback(async () => {
    setYearsLoading(true);
    try {
      const data = await listIndicatoriYears();
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const nextYears = rows
        .map((r) => String(r?.year ?? '').trim())
        .filter(Boolean);
      setYears(nextYears);
      setRowsError('');
      setSelectedYear((prev) => {
        if (prev && nextYears.includes(String(prev))) return prev;
        return nextYears[0] || null;
      });
    } catch (e) {
      setRowsError('Errore nel caricamento degli anni');
    } finally {
      setYearsLoading(false);
    }
  }, []);

  const loadRows = useCallback(async ({ dataInizioTrimestre, year, searchText }) => {
    const trimmedTrimestre = String(dataInizioTrimestre || '').trim();
    const trimmedYear = String(year || '').trim();
    if (!trimmedTrimestre && !trimmedYear) {
      setRows([]);
      return;
    }
    setRowsLoading(true);
    try {
      const from = trimmedYear ? `${trimmedYear}-01-01` : '';
      const to = trimmedYear ? `${trimmedYear}-12-31` : '';
      const data = await listIndicatori({
        data_inizio_trimestre: trimmedTrimestre,
        search: searchText || '',
        from: trimmedTrimestre ? '' : from,
        to: trimmedTrimestre ? '' : to
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setRowsError('');
    } catch (e) {
      setRowsError('Errore nel caricamento dei dati');
    } finally {
      setRowsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrimesters();
  }, [loadTrimesters]);

  useEffect(() => {
    loadYears();
  }, [loadYears]);

  useEffect(() => {
    const dataInizioTrimestre = selectedTrimestre?.data_inizio_trimestre || '';
    const year = selectedYear || '';
    if (!dataInizioTrimestre && !year) return;
    const timeoutId = setTimeout(() => {
      loadRows({ dataInizioTrimestre, year, searchText: search });
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [loadRows, search, selectedTrimestre?.data_inizio_trimestre, selectedYear]);

  useEffect(() => {
    if (selectedYear) return;
    const inferred = extractYear(selectedTrimestre?.data_inizio_trimestre);
    if (inferred) setSelectedYear(inferred);
  }, [selectedTrimestre?.data_inizio_trimestre, selectedYear]);

  const visibleTrimesters = useMemo(() => {
    if (!selectedYear) return trimesters;
    const y = String(selectedYear);
    return (trimesters || []).filter((t) => extractYear(t?.data_inizio_trimestre) === y);
  }, [selectedYear, trimesters]);

  const trimestreLabel = useMemo(() => {
    if (!selectedTrimestre?.data_inizio_trimestre) return '';
    return `${formatDateIt(selectedTrimestre.data_inizio_trimestre)} → ${formatDateIt(selectedTrimestre.data_fine_trimestre)}`;
  }, [selectedTrimestre?.data_fine_trimestre, selectedTrimestre?.data_inizio_trimestre]);

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [page, rows, rowsPerPage]);

  const handleOpenEdit = useCallback(
    (row = null) => {
      if (!selectedTrimestre?.data_inizio_trimestre) {
        toast.error('Seleziona prima un trimestre');
        return;
      }
      setEditRow(row);
      if (row) {
        setEditForm({
          data_inizio_trimestre: row.data_inizio_trimestre || selectedTrimestre.data_inizio_trimestre,
          data_fine_trimestre: row.data_fine_trimestre || selectedTrimestre.data_fine_trimestre,
          commissione_massimo_scoperto_media: row.commissione_massimo_scoperto_media ?? '',
          maggiorazione_mora_media: row.maggiorazione_mora_media ?? '',
          fonte_file: normalizeText(row.fonte_file || 'manuale') || 'manuale'
        });
      } else {
        setEditForm({
          data_inizio_trimestre: selectedTrimestre.data_inizio_trimestre,
          data_fine_trimestre: selectedTrimestre.data_fine_trimestre,
          commissione_massimo_scoperto_media: '',
          maggiorazione_mora_media: '',
          fonte_file: 'manuale'
        });
      }
      setEditOpen(true);
    },
    [selectedTrimestre?.data_fine_trimestre, selectedTrimestre?.data_inizio_trimestre]
  );

  const handleSaveEdit = useCallback(async () => {
    const payload = {
      data_inizio_trimestre: normalizeDateToIso(editForm.data_inizio_trimestre),
      data_fine_trimestre: normalizeDateToIso(editForm.data_fine_trimestre),
      commissione_massimo_scoperto_media:
        editForm.commissione_massimo_scoperto_media === '' ? null : Number(editForm.commissione_massimo_scoperto_media),
      maggiorazione_mora_media: editForm.maggiorazione_mora_media === '' ? null : Number(editForm.maggiorazione_mora_media),
      fonte_file: normalizeText(String(editForm.fonte_file || '').trim()) || 'manuale'
    };

    if (!payload.data_inizio_trimestre || !payload.data_fine_trimestre) {
      toast.error('Compila correttamente le date');
      return;
    }
    if (payload.commissione_massimo_scoperto_media !== null && !Number.isFinite(payload.commissione_massimo_scoperto_media)) {
      toast.error('CMS non valida');
      return;
    }
    if (payload.maggiorazione_mora_media !== null && !Number.isFinite(payload.maggiorazione_mora_media)) {
      toast.error('Mora non valida');
      return;
    }

    setEditLoading(true);
    try {
      if (editRow?.id) {
        await updateIndicatori(editRow.id, payload);
        toast.success('Riga aggiornata');
      } else {
        await createIndicatori(payload);
        toast.success('Riga creata');
      }
      setEditOpen(false);
      await loadRows({
        dataInizioTrimestre: selectedTrimestre?.data_inizio_trimestre,
        searchText: search
      });
    } catch (e) {
      toast.error('Salvataggio non riuscito');
    } finally {
      setEditLoading(false);
    }
  }, [
    editForm.commissione_massimo_scoperto_media,
    editForm.data_fine_trimestre,
    editForm.data_inizio_trimestre,
    editForm.fonte_file,
    editForm.maggiorazione_mora_media,
    editRow?.id,
    loadRows,
    search,
    selectedTrimestre?.data_inizio_trimestre
  ]);

  const handleOpenImport = useCallback(() => {
    setImportFile(null);
    setImportOpen(true);
  }, []);

  const handleOpenOfficialSource = useCallback(() => {
    window.open(OFFICIAL_TEGM_URL, '_blank', 'noopener,noreferrer');
  }, []);

  const handleCopyOfficialSource = useCallback(async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(OFFICIAL_TEGM_URL);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = OFFICIAL_TEGM_URL;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      toast.success('Link copiato');
    } catch {
      toast.error('Copia non riuscita');
    }
  }, []);

  const handleOpenDelete = useCallback((row) => {
    setDeleteTarget(row || null);
    setDeleteOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const id = deleteTarget?.id;
    if (!id) return;
    setDeleteLoading(true);
    try {
      await deleteIndicatori(id);
      toast.success('Riga eliminata');
      setDeleteOpen(false);
      await loadRows({
        dataInizioTrimestre: selectedTrimestre?.data_inizio_trimestre,
        searchText: search
      });
    } catch (e) {
      toast.error('Eliminazione non riuscita');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget?.id, loadRows, search, selectedTrimestre?.data_inizio_trimestre]);

  const handleConfirmImport = useCallback(async () => {
    if (!importFile) {
      toast.error('Seleziona un file CSV');
      return;
    }
    setImportLoading(true);
    try {
      const data = await importIndicatoriCsv(importFile);
      const count = Number(data?.insertedOrUpdated || 0);
      toast.success(`Import completato: ${count} righe inserite/aggiornate`);

      const nextTrimesters = Array.isArray(data?.trimesters) ? data.trimesters : [];
      if (nextTrimesters.length) {
        setTrimesters(nextTrimesters);
        setSelectedTrimestre((prev) => {
          if (prev?.data_inizio_trimestre) {
            const stillExists = nextTrimesters.some((t) => t.data_inizio_trimestre === prev.data_inizio_trimestre);
            if (stillExists) return prev;
          }
          return nextTrimesters[0] || null;
        });
      } else {
        await loadTrimesters();
      }
      setImportOpen(false);
    } catch (e) {
      toast.error('Import non riuscito');
    } finally {
      setImportLoading(false);
    }
  }, [importFile, loadTrimesters]);

  const handleOpenReport = useCallback(() => {
    const today = getTodayYmd();
    const year = String(selectedYear || '').trim();
    const fallbackFrom = year ? `${year}-01-01` : today;
    const fallbackTo = year ? `${year}-12-31` : today;
    setReportFrom(String(selectedTrimestre?.data_inizio_trimestre || reportFrom || fallbackFrom).slice(0, 10));
    setReportTo(String(selectedTrimestre?.data_fine_trimestre || reportTo || fallbackTo).slice(0, 10));
    setReportOpen(true);
  }, [reportFrom, reportTo, selectedTrimestre?.data_fine_trimestre, selectedTrimestre?.data_inizio_trimestre, selectedYear]);

  const handleConfirmReport = useCallback(async () => {
    if (!reportFrom || !reportTo) {
      toast.error('Compila l’intervallo di riferimento');
      return;
    }
    setReporting(true);
    try {
      const response = await downloadIndicatoriReportPdf({
        data_inizio_trimestre: '',
        search,
        reference_from: reportFrom,
        reference_to: reportTo
      });
      const contentDisposition = response.headers?.['content-disposition'];
      const fallbackName = `indicatori_trimestrali_${String(reportFrom).slice(0, 10)}_${String(reportTo).slice(0, 10)}.pdf`;
      const filename = extractFilename(contentDisposition, fallbackName);
      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report PDF scaricato');
      setReportOpen(false);
    } catch (e) {
      let message = 'Errore generazione report';
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = String(parsed.message);
        } catch {}
      } else if (e?.response?.data?.message) {
        message = String(e.response.data.message);
      }
      toast.error(message);
    } finally {
      setReporting(false);
    }
  }, [reportFrom, reportTo, search, selectedTrimestre?.data_inizio_trimestre]);

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.26 : 0.14),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 auto'
                    }}
                  >
                    <BarChartIcon size={18} color={accentColor} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                      Indicatori Trimestrali
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Area dedicata alla gestione e all’aggiornamento degli indicatori trimestrali utilizzati nelle generazioni.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                  <Tooltip title="Info tabella">
                    <IconButton
                      size="small"
                      onClick={() => setInfoOpen(true)}
                      sx={{
                        color: accentColor,
                        bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                        border: '1px solid',
                        borderColor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.35 : 0.28)
                      }}
                    >
                      <InfoIcon size={18} />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon size={16} />}
                    onClick={handleOpenReport}
                    disabled={reporting}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Report
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadCloudIcon size={16} />}
                    onClick={handleOpenImport}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Importa CSV
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlusIcon size={16} />}
                    onClick={() => handleOpenEdit(null)}
                    disabled={!selectedTrimestre?.data_inizio_trimestre}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Nuovo
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      <Dialog open={officialSourceOpen} onClose={() => setOfficialSourceOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.26 : 0.14),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto'
              }}
            >
              <InfoIcon size={18} color={accentColor} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }} noWrap>
                Aggiornamento dati · Fonte ufficiale
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Il recupero dei dati aggiornati va effettuato sul sito Banca d’Italia
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2.25 }}>
                  <Typography sx={{ fontWeight: 800 }}>Link ufficiale</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'nowrap' }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={OFFICIAL_TEGM_URL}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copia link">
                              <IconButton size="small" onClick={handleCopyOfficialSource}>
                                <CopyIcon size={16} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleOpenOfficialSource}
                      startIcon={<ExternalLinkIcon size={16} />}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Apri sito
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto'
                      }}
                    >
                      <DownloadIcon size={18} color={accentColor} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }}>Passo 1</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Scarica il file aggiornato dal portale ufficiale.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto'
                      }}
                    >
                      <UploadCloudIcon size={18} color={accentColor} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }}>Passo 2</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Importa il CSV qui per aggiornare il database locale.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setOfficialSourceOpen(false)}>
            Ho capito
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportOpen} onClose={() => (reporting ? null : setReportOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Report PDF</DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            Indica l’intervallo di riferimento da riportare nel PDF.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <TextField
              type="date"
              size="small"
              fullWidth
              label="Da"
              value={reportFrom}
              onChange={(e) => setReportFrom(e.target.value)}
            />
            <TextField
              type="date"
              size="small"
              fullWidth
              label="A"
              value={reportTo}
              onChange={(e) => setReportTo(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setReportOpen(false)} disabled={reporting}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleConfirmReport} disabled={reporting}>
            {reporting ? 'Report…' : 'Genera PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.26 : 0.14),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto'
              }}
            >
              <BarChartIcon size={18} color={accentColor} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }} noWrap>
                Indicatori trimestrali
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tabella: gtl_indicatori_trimestrali
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" variant="outlined" label="Dati per trimestre" sx={{ fontWeight: 800 }} />
            <Chip size="small" variant="outlined" label="Indicatori usati in generazione" sx={{ fontWeight: 800 }} />
          </Box>

          <Grid container spacing={2} sx={{ mt: 0.75 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto'
                      }}
                    >
                      <CalendarRangeIcon size={18} color={accentColor} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }}>Periodo</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Una riga per trimestre (data inizio/fine).
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto'
                      }}
                    >
                      <PercentIcon size={18} color={accentColor} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }}>Indicatori</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        CMS media (%) e maggiorazione mora media (%).
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={12}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto'
                      }}
                    >
                      <FileTextIcon size={18} color={accentColor} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }}>Fonte e tracciabilità</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Nome file/import e data importazione per audit e controlli.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setInfoOpen(false)}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mt: 0.25 }}>
        <Grid item xs={12}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap', overflowX: 'auto' }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 160 }}
                    loading={yearsLoading}
                    options={years}
                    value={selectedYear}
                    onChange={(_e, value) => {
                      setSelectedYear(value || null);
                      setSelectedTrimestre(null);
                      setRows([]);
                      setRowsError('');
                      setPage(0);
                    }}
                    getOptionLabel={(opt) => String(opt || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Anno"
                        placeholder="Seleziona…"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {yearsLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 320 }}
                    loading={trimestersLoading}
                    options={visibleTrimesters}
                    value={selectedTrimestre}
                    onChange={(_e, value) => {
                      setSelectedTrimestre(value || null);
                      if (value?.data_inizio_trimestre) {
                        const inferredYear = extractYear(value.data_inizio_trimestre);
                        if (inferredYear) setSelectedYear(inferredYear);
                      }
                      if (!value) {
                        setRows([]);
                        setRowsError('');
                      }
                      setPage(0);
                    }}
                    getOptionLabel={(opt) =>
                      opt?.data_inizio_trimestre
                        ? `${formatDateIt(opt.data_inizio_trimestre)} → ${formatDateIt(opt.data_fine_trimestre)}`
                        : ''
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Trimestre"
                        placeholder="Seleziona…"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {trimestersLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />

                  <TextField
                    size="small"
                    sx={{ minWidth: 260 }}
                    placeholder="Cerca…"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(0);
                    }}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon size={16} />
                        </InputAdornment>
                      )
                    }}
                  />

                  <Chip size="small" label={`${rows.length} righe`} variant="outlined" sx={{ fontWeight: 700 }} />
                  {selectedYear ? <Chip size="small" label={`Anno: ${selectedYear}`} variant="outlined" sx={{ fontWeight: 700 }} /> : null}
                  {trimestreLabel ? <Chip size="small" label={trimestreLabel} variant="outlined" sx={{ fontWeight: 700 }} /> : null}
              </Box>

              {rowsError ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {rowsError}
                </Alert>
              ) : null}

              <Box sx={{ mt: 2 }}>
                <TableContainer>
                  <Table
                    size="small"
                    sx={{
                      '& .MuiTableCell-root': {
                        borderBottomColor: 'divider'
                      },
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        color: 'text.secondary',
                        fontSize: 13,
                        whiteSpace: 'nowrap'
                      },
                      '& .MuiTableCell-body': {
                        fontSize: 13,
                        py: 1.25
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Inizio</TableCell>
                        <TableCell>Fine</TableCell>
                        <TableCell align="right">CMS media</TableCell>
                        <TableCell align="right">Mora media</TableCell>
                        <TableCell>Fonte</TableCell>
                        <TableCell>Importato</TableCell>
                        <TableCell align="right">Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowsLoading ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={18} />
                              <Typography variant="body2" color="text.secondary">
                                Caricamento…
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : null}

                      {!rowsLoading && visibleRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="text.secondary">
                              Nessun dato per il trimestre selezionato
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}

                      {!rowsLoading
                        ? visibleRows.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{formatDateIt(row.data_inizio_trimestre)}</TableCell>
                              <TableCell>{formatDateIt(row.data_fine_trimestre)}</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                  {formatPercent(row.commissione_massimo_scoperto_media)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                  {formatPercent(row.maggiorazione_mora_media)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 220 }}>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {normalizeText(row.fonte_file) || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {formatDateTimeIt(row.imported_at)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Modifica">
                                  <IconButton size="small" onClick={() => handleOpenEdit(row)} sx={{ color: 'primary.main' }}>
                                    <EditIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Elimina">
                                  <IconButton size="small" onClick={() => handleOpenDelete(row)} sx={{ color: 'error.main' }}>
                                    <DeleteIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        : null}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={rows.length}
                  page={page}
                  onPageChange={(_e, nextPage) => setPage(nextPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Righe per pagina"
                  sx={{
                    '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.25 },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: 13 }
                  }}
                />
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      <Dialog open={editOpen} onClose={() => (editLoading ? null : setEditOpen(false))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>{editRow?.id ? 'Modifica riga' : 'Nuova riga'}</DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Inizio trimestre"
                value={formatDateItInput(editForm.data_inizio_trimestre)}
                onChange={(e) => setEditForm((prev) => ({ ...prev, data_inizio_trimestre: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fine trimestre"
                value={formatDateItInput(editForm.data_fine_trimestre)}
                onChange={(e) => setEditForm((prev) => ({ ...prev, data_fine_trimestre: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CMS media (%)"
                value={editForm.commissione_massimo_scoperto_media}
                onChange={(e) => setEditForm((prev) => ({ ...prev, commissione_massimo_scoperto_media: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maggiorazione mora media (%)"
                value={editForm.maggiorazione_mora_media}
                onChange={(e) => setEditForm((prev) => ({ ...prev, maggiorazione_mora_media: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fonte"
                value={editForm.fonte_file}
                onChange={(e) => setEditForm((prev) => ({ ...prev, fonte_file: e.target.value }))}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setEditOpen(false)} disabled={editLoading}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editLoading}>
            {editLoading ? 'Salvataggio…' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => (importLoading ? null : setImportOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Import CSV</DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
            <Button variant="outlined" component="label" size="small" disabled={importLoading}>
              Seleziona file
              <input
                hidden
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
              {importFile?.name || 'Nessun file selezionato'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setImportOpen(false)} disabled={importLoading}>
            Chiudi
          </Button>
          <Button variant="contained" onClick={handleConfirmImport} disabled={importLoading}>
            {importLoading ? 'Import…' : 'Importa'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => (deleteLoading ? null : setDeleteOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Conferma eliminazione</DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            Eliminare la riga selezionata? Operazione irreversibile.
          </Typography>

          <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: '1fr', rowGap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {deleteTarget?.data_inizio_trimestre
                ? `${formatDateIt(deleteTarget.data_inizio_trimestre)} → ${formatDateIt(deleteTarget.data_fine_trimestre)}`
                : '—'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                variant="outlined"
                label={`CMS ${formatPercent(deleteTarget?.commissione_massimo_scoperto_media, 3)}`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Mora ${formatPercent(deleteTarget?.maggiorazione_mora_media, 3)}`}
                sx={{ fontWeight: 700 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
              Fonte: {normalizeText(deleteTarget?.fonte_file) || '—'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione…' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
